import React, { useState, useEffect, useCallback } from 'react';
import styles from './styles/UploadHistory.module.css';
import { useHistory, useLocation } from "react-router-dom";
import customFetch from "../apicall/api";

const UploadHistory = () => {
  const [batches, setBatches] = useState([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [submittedSearchTerm, setSubmittedSearchTerm] = useState('');
  const [accessMessage, setAccessMessage] = useState(null);
  const history = useHistory();
  const location = useLocation();

  const { api_token, effective_designation_id } = location.state || {};
  // It's safer to use the one from state if available, or fall back to localStorage.
  const final_api_token = api_token || localStorage.getItem("api_token");
  // const final_effective_designation_id = effective_designation_id || localStorage.getItem("effective_designation_id");
  const final_effective_designation_id = effective_designation_id;

  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0
  });

  const [sortConfig, setSortConfig] = useState({
    sortBy: 'created_at', // Default sorting column
    sortOrder: 'desc'     // Default sorting direction
  });

  // Combine relevant dependencies into a single object for the useEffect dependency array
  const dependencyKey = [pagination.page, sortConfig.sortBy, sortConfig.sortOrder, final_api_token, submittedSearchTerm];

  useEffect(() => {
    // Determine the loading state based on whether this is the very first load
    const loadingStateSetter = isInitialLoading ? setIsInitialLoading : setIsTableLoading;

    // Call loadBatches with the appropriate loading state setter
    loadBatches(pagination.page, pagination.pageSize, sortConfig.sortBy, sortConfig.sortOrder, submittedSearchTerm, loadingStateSetter);
  }, dependencyKey);

  const loadBatches = useCallback(async (page, pageSize, sortBy, sortOrder, search, setLoadingState) => {
    try {
      setLoadingState(true);
      // Construct the query parameters
      const params = new URLSearchParams({
        page: page,
        page_size: pageSize,
        sort_by: sortBy,
        sort_order: sortOrder,
        effective_designation_id: final_effective_designation_id
      });

      // *** NEW: Add search query if it exists ***
      if (search) {
          params.append('upload_request_id', search);
      }

      // const response = await fetch(`http://localhost:5000/bulk-file-upload/list/files?${params.toString()}`, {
      //   method: "GET",
      //   headers: {"api-token": final_api_token },
      // });

      const response = await customFetch(
        process.env.REACT_APP_URL + `/bulk-file-upload/list/files?${params.toString()}`,
        {
          method: "GET",
          headers: {
            "api-token": final_api_token,
          },
        }
      );

      if (!response.ok) {
        const text = await response.text().catch(() => "");
        throw new Error(`Failed to fetch data: ${response.status} ${text}`);
      }

      const data = await response.json();
      if (data.status === 1) {
        if (data.message && data.records && data.records.length === 0) {
            setAccessMessage(data.message);
            setBatches([]); // Ensure batches are empty
        } else{
          setAccessMessage(null);
          setBatches(data.data);
          setPagination({
            page: data.page,
            pageSize: data.page_size,
            totalCount: data.total_count,
            totalPages: data.total_pages
          });
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoadingState(false);
    }
  }, [final_api_token, isInitialLoading, setIsInitialLoading]);

  const handleViewDetails = (batch) => {
    // navigate(`/batch/${batch.upload_request_id}`);
    // /bulk-file-upload/record-history
    console.log("api_token for record page:", final_api_token);
    console.log("effective-designation-id for record page:", final_effective_designation_id);
    console.log("details page for record:", batch.upload_request_id)
    history.push({
      pathname: `/bulk-file-upload/record-history/${batch.upload_request_id}`,
      state: { api_token: final_api_token, effective_designation_id: final_effective_designation_id } 
    });
  };

  // *** NEW: Search handler ***
  const handleSearch = () => {
      // 1. Reset page to 1 for a new search
      setSubmittedSearchTerm(searchQuery);
      setPagination(p => ({ ...p, page: 1 }));
      // 2. The useEffect hook (above) will now see that `pagination.page` changed 
      // (or at least, the next render will use the new state) and trigger 
      // loadBatches with the current `searchQuery`.
      // NOTE: We rely on the useEffect trigger for the actual API call when we change state.
      // If the page is already 1, we must explicitly call loadBatches:
      // if (pagination.page === 1) {
      //     loadBatches(1, pagination.pageSize, sortConfig.sortBy, sortConfig.sortOrder, searchQuery, setIsTableLoading);
      // }
  };

  // Handler for Enter key press in the input
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      // Only change page, useEffect will trigger loadBatches
      setIsTableLoading(true);
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  const handleSort = (column) => {
    setSortConfig(prev => {
      let order = 'desc';
      
      // If the same column is clicked, toggle direction
      if (prev.sortBy === column && prev.sortOrder === 'desc') {
        order = 'asc';
      } else if (prev.sortBy === column && prev.sortOrder === 'asc') {
        // Option to cycle back to default/remove sort if clicked a third time
        // For simplicity, we stick to toggling asc/desc
      }
      // Reset page to 1 whenever sorting changes
      setIsTableLoading(true);
      setPagination(p => ({ ...p, page: 1 })); 
      
      return {
        sortBy: column,
        sortOrder: order
      };
    });
  };

  const getSortIndicator = (column) => {
    if (sortConfig.sortBy !== column) return '';
    return sortConfig.sortOrder === 'asc' ? ' ▲' : ' ▼';
  };


  const formatStatusText = (statusText) => {
    if (!statusText) return '';
    
    // 1. Replace all underscores with spaces
    let formattedText = statusText.replace(/_/g, ' ');

    // 2. Capitalize the first letter of each word
    formattedText = formattedText.toLowerCase().split(' ').map((word) => {
        if (word.length === 0) return '';
        // Capitalize only the first letter of the word
        return word.charAt(0).toUpperCase() + word.slice(1);
    }).join(' ');

    return formattedText;
  };

  const getStatusClass = (status) => {
    // We expect the raw, unformatted status here (e.g., 'file_uploaded')
    const normalizedStatus = status ? status.toLowerCase() : '';
    
    switch (normalizedStatus) {
      case 'file_uploaded':
      case 'completed':
        return styles.statusCompleted;
        
      case 'processing':
        return styles.statusProcessing;
        
      case 'approved': 
        return styles.statusApproved;
        
      case 'presigned_url_generated': 
        return styles.statusInitiated; // Reusing 'statusInitiated' for this color/style
        
      default:
        return styles.statusDefault;
    }
  };


  /**
  * Formats an Epoch timestamp (in seconds) to DD/MM/YYYY.
  */
  const formatDisplayDate = (epochSeconds) => {
    // Check for null, undefined, or zero
    if (!epochSeconds) {
        return '-';
    }
    
    try {
        // 1. Convert seconds (int) to milliseconds (required by JS Date constructor)
        const milliseconds = epochSeconds * 1000; 
        
        const date = new Date(milliseconds);

        // Check if the date is valid (can happen if the epoch value is corrupted)
        if (isNaN(date.getTime())) {
            return 'Invalid Date'; 
        }

        // Get day, month, and year with padding
        const day = String(date.getDate()).padStart(2, '0');
        // Month is 0-indexed, so add 1
        const month = String(date.getMonth() + 1).padStart(2, '0'); 
        const year = date.getFullYear();

        return `${day}/${month}/${year}`;
    } catch (e) {
        console.error("Date formatting failed:", e);
        return '-';
    }
  };
  
  

  const handleNewFileUpload = () => {
  // Use simple default values for the state if needed
  console.log("api_token 2:", final_api_token);
  console.log("effective-designation-id 2:", final_effective_designation_id);
  history.push({
      pathname: "/bulk-file-upload/select-profile",
      state: { api_token: final_api_token, effective_designation_id: final_effective_designation_id } 
    });
  };


  if (isInitialLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading data...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>File uploads</h1>
        <div className={styles.headerActions}>
          <div className={styles.searchWrapper}>
            {/* Search Input */}
            <input
              type="text"
              placeholder="Search by Upload Request ID"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyPress}
              className={styles.searchInput}
            />
             {/* Search Button (Black and minimal) */}
             <button className={styles.searchButton} onClick={handleSearch}>Search</button>
          </div>
          
          {/* Dropdowns (Mimicking the reference image) */}
          {/* <select className={styles.dropdown}>
            <option>All Partners</option>
          </select>
          <select className={styles.dropdown}>
            <option>All Statuses</option>
          </select> */}
          
          {/* Reset Button */}
          {/* <button className={styles.resetButton}>Reset</button> */}
          <button 
            className={styles.uploadNewFileButton}
            onClick={handleNewFileUpload}
            disabled={!!accessMessage}
          >
            Upload New File
          </button>

        </div>
      </div>

      <div className={styles.tableWrapper}>
        {/* Conditional Rendering for Table Loading Overlay */}
        { accessMessage ? (
          <div className={styles.noAccessMessage}>
            <p>{accessMessage}</p>
          </div>
        ) : batches.length === 0 && !isTableLoading ? (
          // Display standard No Records Found message only if access is okay and no results
          <div className={styles.noRecordsMessage}>
            No file upload records found.
          </div>
        ) : (
        <>
        {isTableLoading && (
            <div className={styles.tableOverlay}>
                <div className={styles.tableLoader}>Loading...</div>
            </div>
        )}
        <table className={styles.table}>
          <thead>
            <tr>
              {/* Added onClick handlers to headers for sorting */}
              <th onClick={() => handleSort('upload_request_id')} className={styles.sortableHeader}>
                Upload Request ID {getSortIndicator('upload_request_id')}
              </th>
              <th onClick={() => handleSort('status')} className={styles.sortableHeader}>
                Status {getSortIndicator('status')}
              </th>
              <th onClick={() => handleSort('process_type')} className={styles.sortableHeader}>
                Process Type {getSortIndicator('process_type')}
              </th>
              <th>Files Included</th>
              <th onClick={() => handleSort('created_at')} className={styles.sortableHeader}>
                Date {getSortIndicator('created_at')}
              </th>
            </tr>
          </thead>
          <tbody>
            {batches.map((batch) => {
                // Get the RAW status first, as it's needed for the CSS class mapping
                const rawBatchStatus = batch.status || (batch.files_data && batch.files_data.length > 0 ? batch.files_data[0].status : 'unknown');

                // Get the DISPLAY text using the formatting helper
                const displayBatchStatus = formatStatusText(rawBatchStatus);

                return (
                  // Make the entire row clickable
                  <tr key={batch.upload_request_id} className={styles.tableRow} onClick={() => handleViewDetails(batch)}>
                    {/* Batch ID */}
                    <td className={styles.batchIdCell}>
                      {batch.upload_request_id}
                    </td>
                    
                    {/* Status Badge */}
                    <td className={styles.statusCell}>
                      <span className={`${styles.statusBadge} ${getStatusClass(rawBatchStatus)}`}>
                        {displayBatchStatus}
                      </span>
                    </td>
                    
                    {/* Process Type */}
                    <td className={styles.processTypeCell}>
                        {batch.process_type}
                    </td>

                    {/* Files Included (List of names) */}
                    <td>
                      <div className={styles.filesList}>
                        {batch.files_data.map((file, index) => {
                          const fullName = file.system_generated_filename;
                          const firstUnderscoreIndex = fullName.indexOf('_');
                          const fileCode = firstUnderscoreIndex !== -1 
                                        ? fullName.substring(0, firstUnderscoreIndex)
                                        : fullName.split('.')[0];
                          return (
                              <div key={index} className={styles.fileName}>
                                  {/* Display the extracted File Code */}
                                  {fileCode}</div>
                          );
                        })}
                      </div>
                    </td>
                    
                    {/* Date */}
                    <td className={styles.dateCell}>
                      {formatDisplayDate(batch.created_at)}
                    </td>
                  </tr>
                );
            })}
          </tbody>
        </table>
        </>
      )}
      </div>

      <div className={styles.paginationControls}>
        <span className={styles.paginationText}>
          Showing {((pagination.page - 1) * pagination.pageSize) + 1} - 
          {Math.min(pagination.page * pagination.pageSize, pagination.totalCount)} of {pagination.totalCount} Entries
        </span>
        
        <div className={styles.paginationButtons}>
          <button 
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page === 1}
            className={styles.pageButton}
          >
            {'<'} {/* Left Arrow */}
          </button>
          
          <span className={styles.pageNumber}>
            Page {pagination.page} / {pagination.totalPages}
          </span>
          
          <button 
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page >= pagination.totalPages}
            className={styles.pageButton}
          >
            {'>'} {/* Right Arrow */}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadHistory;